<script lang="ts">
	/**
	 * `/Applications` — the registry of who may read entitlement state (Design §4.3, §8.3).
	 *
	 * Two things on this screen are unlike every other admin form, and both are deliberate:
	 *
	 * - **A credential is shown once.** The API key and the signing secret exist in this browser
	 *   for exactly as long as the panel below is open, and no read endpoint will ever return
	 *   either again. So the panel says so, loudly, rather than looking like an ordinary field.
	 * - **`subscribedEntitlementKeys` is an authorization scope, not a filter.** An empty list
	 *   means the application sees *nothing*, and `allKeys` is an explicit, audited opt-in that
	 *   turns a scoped integration into a reader of every product's customers — so it gets a
	 *   two-step confirm rather than a toggle.
	 */

	import { onMount } from 'svelte';
	import {
		Badge,
		Button,
		Checkbox,
		Input,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell,
		Toggle
	} from 'flowbite-svelte';
	import {
		ConfirmButton,
		EmptyState,
		FormField,
		FormModal,
		PageHeader,
		Panel,
		RelativeDate,
		SelectField,
		CIDRListEditor,
		StringListEditor
	} from '../../components';
	import {
		createApplication,
		listAdminProducts,
		listApplications,
		rotateApplicationKey,
		rotateApplicationSecret,
		updateApplication,
		listAbstractIDApplications
	} from '../../AppNetwork';
	import type {
		AbstractIDApplication,
		IssuedApplicationCredential,
		Product,
		StoreApplication
	} from '../../AppTypes';
	import { toastError, toastSuccess } from '../../stores/toast';
	import WebhookDeliveryTable from './applications/WebhookDeliveryTable.svelte';

	let applications: StoreApplication[] = [];
	let loading: boolean = true;
	let loadError: string = '';
	let busyUUID: string = '';

	let isAdding: boolean = false;
	let creating: boolean = false;
	let draft = { abstractIDAppUUID: '', name: '', webhookURL: '', allowedIPs: [] as string[] };
	let draftIPsValid: boolean = true;

	/**
	 * AbstractID's application directory, read through the store's own application key.
	 *
	 * Treated as optional throughout: `available: false` with a reason falls back to the uuid
	 * field rather than failing the screen, because a picker is a convenience over an input that
	 * still works.
	 */
	let directoryAvailable: boolean = false;
	let directoryReason: string = "The application's identity, as AbstractID knows it.";
	let directoryLoading: boolean = true;
	let directoryApplications: AbstractIDApplication[] = [];

	/**
	 * Lets the operator type a uuid even when the picker is working.
	 *
	 * The picker can be *available yet unable to offer the right application*: the directory is read
	 * one page deep, and every application already registered is filtered out. Without this, both
	 * cases are a dead end on a screen that otherwise looks healthy — a select with nothing in it
	 * and no way forward. The field the picker replaced is always one click away.
	 */
	let uuidEntryManual: boolean = false;

	// Show the picker only when it can actually offer something.
	$: pickerUsable = directoryAvailable && !uuidEntryManual && (directoryLoading || unregistered.length > 0);

	$: directoryItems = unregistered.map(application => ({
		value: application.uuid,
		name: `${application.name}${application.isActive ? '' : ' (inactive)'}`,
		// The uuid on its own line: two applications may share a name, and the operator is choosing
		// an identity, not a label.
		description: application.uuid
	}));

	// Registering the same application twice is refused by the server's unique index; hiding the
	// ones already registered means the operator never reaches that error.
	$: unregistered = directoryApplications.filter(
		candidate => !applications.some(row => row.abstractIDAppUUID === candidate.uuid)
	);

	async function fnLoadDirectory(): Promise<void> {
		directoryLoading = true;
		try {
			const directory = await listAbstractIDApplications();
			directoryAvailable = directory.available;
			directoryApplications = directory.items;
			if (!directory.available && directory.reason !== null) {
				directoryReason = directory.reason;
			}
		} catch (error) {
			directoryAvailable = false;
			directoryReason = (error as Error).message;
		} finally {
			directoryLoading = false;
		}
	}

	// Names the chosen application by default, so the operator is not asked to retype it.
	$: if (draft.abstractIDAppUUID !== '' && draft.name === '') {
		const chosen = directoryApplications.find(row => row.uuid === draft.abstractIDAppUUID);
		if (chosen !== undefined) {
			draft.name = chosen.name;
		}
	}

	/** The one thing on this screen that is never fetched again. */
	let issued: IssuedApplicationCredential | null = null;
	let issuedFor: string = '';

	/** The row whose scope is open for editing, and the working copy of its key list. */
	let editingUUID: string = '';
	let editingKeys: string[] = [];

	/**
	 * The catalogue, read only so the scope can be **picked rather than typed**.
	 *
	 * An entitlement key is a string an application hard-codes and §4.1 freezes at publication, so
	 * a free-text field here is a screen where one typo silently scopes an integration to a key no
	 * product has — which reads as configured, returns nothing, and logs nothing because nothing
	 * failed. The products are the authoritative list of what a key can be.
	 *
	 * Its failure is swallowed to the manual field below rather than shown: this page's real
	 * subject is the registry, and an operator must not lose the ability to fix a scope because a
	 * catalogue read failed.
	 */
	let catalogueProducts: Product[] = [];
	let catalogueLoading: boolean = true;

	/**
	 * The escape hatch, and the same one the uuid field has for the same reason.
	 *
	 * An application is routinely registered *before* the product it will read exists — that is the
	 * order the work happens in — so a picker that can only offer today's catalogue must not be the
	 * only way to name a key.
	 */
	let keyEntryManual: boolean = false;

	/**
	 * What the scope may be set to: every key in the catalogue, plus any key this application
	 * already holds that no product answers to.
	 *
	 * The second half is the one that matters. A scope naming a key the catalogue has lost — a
	 * product deleted, a draft renamed — must still be **visible and unticked-able**: rendering
	 * only catalogue keys would drop it from the list, and saving would then revoke it silently.
	 */
	$: scopeOptions = fnScopeOptions(catalogueProducts, editingKeys);

	function fnScopeOptions(
		products: Product[],
		selected: string[]
	): { key: string; label: string; isOrphan: boolean }[] {
		const collator = new Intl.Collator();
		const options = products
			.map(product => ({
				key: product.entitlementKey,
				label: product.familyName === null ? product.name : `${product.familyName} · ${product.name}`,
				isOrphan: false
			}))
			.sort((left, right) => collator.compare(left.label, right.label));

		const known = new Set(options.map(option => option.key));
		return [
			...options,
			...selected
				.filter(key => !known.has(key))
				.map(key => ({ key, label: 'No product carries this key', isOrphan: true }))
		];
	}

	function fnToggleKey(key: string, wanted: boolean): void {
		editingKeys = wanted
			? [...new Set([...editingKeys, key])]
			: editingKeys.filter(existing => existing !== key);
	}

	async function fnLoadCatalogue(): Promise<void> {
		catalogueLoading = true;
		try {
			catalogueProducts = (await listAdminProducts()).items;
		} catch {
			// Swallowed on purpose — see above. The manual field is the fallback.
			catalogueProducts = [];
		} finally {
			catalogueLoading = false;
		}
	}

	/**
	 * The application's own details, as opposed to its key scope.
	 *
	 * Registration is not the last time any of these change: an application moves domain, a
	 * webhook path is versioned, an allow-list gains an office. Leaving them create-only means the
	 * only way to correct one is to register a second application and re-issue its credentials.
	 */
	let detailsUUID: string = '';
	let details = { name: '', webhookURL: '', allowedIPs: [] as string[] };

	$: editingDetails = applications.find(app => app.abstractIDAppUUID === detailsUUID) ?? null;
	let detailsIPsValid: boolean = true;
	$: canSaveDetails = details.name.trim().length > 0 && detailsIPsValid;

	function fnStartDetails(application: StoreApplication): void {
		detailsUUID = application.abstractIDAppUUID;
		details = {
			name: application.name,
			webhookURL: application.webhookURL ?? '',
			allowedIPs: [...(application.allowedIPs ?? [])]
		};
	}

	async function fnSaveDetails(): Promise<void> {
		if (editingDetails === null) {
			return;
		}
		await fnPatch(editingDetails, {
			name: details.name.trim(),
			// Empty means pull-only, which is a choice rather than an absence.
			webhookURL: details.webhookURL.trim() === '' ? null : details.webhookURL.trim(),
			allowedIPs: fnAllowlist(details.allowedIPs)
		});
		detailsUUID = '';
	}

	$: canCreate =
		!creating &&
		/^[0-9a-fA-F-]{36}$/.test(draft.abstractIDAppUUID) &&
		draft.name.trim().length > 0 &&
		draftIPsValid;

	/** Blank rows are an editing state, never a stored entry; nothing left means "any address". */
	function fnAllowlist(entries: string[]): string[] | null {
		const kept = entries.map(entry => entry.trim()).filter(entry => entry !== '');
		return kept.length === 0 ? null : kept;
	}

	async function fnLoad(): Promise<void> {
		loading = true;
		loadError = '';
		try {
			applications = (await listApplications()).items;
		} catch (error) {
			loadError = (error as Error).message;
		} finally {
			loading = false;
		}
	}

	async function fnCreate(): Promise<void> {
		creating = true;
		try {
			const result = await createApplication({
				abstractIDAppUUID: draft.abstractIDAppUUID.trim(),
				name: draft.name.trim(),
				webhookURL: draft.webhookURL.trim() === '' ? null : draft.webhookURL.trim(),
				allowedIPs: fnAllowlist(draft.allowedIPs)
			});
			issued = result;
			issuedFor = result.application.name;
			draft = { abstractIDAppUUID: '', name: '', webhookURL: '', allowedIPs: [] };
			isAdding = false;
			toastSuccess(`${result.application.name} registered.`);
			await fnLoad();
		} catch (error) {
			toastError('The application was not registered.', (error as Error).message);
		} finally {
			creating = false;
		}
	}

	async function fnPatch(
		application: StoreApplication,
		changes: Parameters<typeof updateApplication>[1]
	): Promise<void> {
		busyUUID = application.abstractIDAppUUID;
		try {
			const updated = await updateApplication(application.abstractIDAppUUID, changes);
			applications = applications.map(row =>
				row.abstractIDAppUUID === updated.abstractIDAppUUID ? updated : row
			);
			toastSuccess(`${updated.name} saved.`);
		} catch (error) {
			// Re-read so a refused change snaps back to what the server actually holds.
			await fnLoad();
			toastError('The application was not changed.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	async function fnRotate(
		application: StoreApplication,
		rotate: typeof rotateApplicationKey
	): Promise<void> {
		busyUUID = application.abstractIDAppUUID;
		try {
			issued = await rotate(application.abstractIDAppUUID);
			issuedFor = application.name;
			await fnLoad();
			toastSuccess(`${application.name} rotated. The previous value stays valid for the overlap window.`);
		} catch (error) {
			toastError('Nothing was rotated.', (error as Error).message);
		} finally {
			busyUUID = '';
		}
	}

	function fnStartEditingScope(application: StoreApplication): void {
		editingUUID = application.abstractIDAppUUID;
		editingKeys = [...(application.subscribedEntitlementKeys ?? [])];
	}

	async function fnSaveScope(application: StoreApplication): Promise<void> {
		await fnPatch(application, { subscribedEntitlementKeys: editingKeys });
		editingUUID = '';
	}

	function fnCheckedFrom(event: Event): boolean {
		return (event.target as HTMLInputElement).checked;
	}

	function fnNumberOrNull(event: Event): number | null {
		const value = (event.target as HTMLInputElement).value.trim();
		return value === '' ? null : Number(value);
	}

	function scopeLabel(application: StoreApplication): string {
		if (application.allKeys) {
			return 'Every key';
		}
		const keys = application.subscribedEntitlementKeys ?? [];
		return keys.length === 0 ? 'No keys' : keys.join(', ');
	}

	onMount(async () => {
		await fnLoad();
		await fnLoadDirectory();
		await fnLoadCatalogue();
	});
</script>

<PageHeader title="Applications">
	<svelte:fragment slot="description">
		The applications that read entitlement state, the keys each one may see, and the credentials
		they authenticate with.
	</svelte:fragment>
	<svelte:fragment slot="help">
		<strong>The entitlement scope is an authorization boundary</strong>, not a display filter. An
		application with no keys sees nothing at all, and <strong>Every key</strong> lets it read every
		user's entitlements for every product in the store, including products belonging to unrelated
		applications. Rotation keeps the previous credential valid for an overlap window, so it never
		needs a coordinated redeploy.
	</svelte:fragment>
</PageHeader>

{#if issued !== null}
	<!-- The one panel on this screen whose contents cannot be fetched again. -->
	<Panel title="Copy these now: they will not be shown again" faIcon="key" class="mb-4">
		<div class="space-y-3">
			<p class="text-default">
				{issuedFor}'s credentials exist in this page and nowhere else you can read them: the store
				keeps only a peppered HMAC of the key, and no endpoint returns either value.
			</p>
			{#if issued.apiKey}
				<FormField id="issued-key" label="API key" description="Sent as the X-Store-API-Key header.">
					<Input id="issued-key" readonly value={issued.apiKey} class="font-mono" />
				</FormField>
			{/if}
			{#if issued.signingSecret}
				<FormField
					id="issued-secret"
					label="Webhook signing secret"
					description="The application can also fetch this itself from /api/v1/application/signing-secrets."
				>
					<Input id="issued-secret" readonly value={issued.signingSecret} class="font-mono" />
				</FormField>
			{/if}
			{#if issued.previousExpiresAt}
				<p class="text-default text-sm">
					The previous value keeps working until
					<RelativeDate date={issued.previousExpiresAt} />.
				</p>
			{/if}
			<Button color="alternative" size="sm" on:click={() => (issued = null)}>I have copied them</Button>
		</div>
	</Panel>
{/if}

{#if loading}
	<EmptyState variant="loading" title="Loading applications…" />
{:else if loadError.length > 0}
	<EmptyState variant="error" title="Applications could not be loaded." description={loadError}>
		<Button color="alternative" size="sm" on:click={fnLoad}>Try again</Button>
	</EmptyState>
{:else}
	<div class="space-y-4">
		<Panel title="Registered applications" faIcon="plug">
			<svelte:fragment slot="titleRight">
				<Button size="sm" color="primary" on:click={() => (isAdding = !isAdding)}>
					<i class="fa-solid fa-plus mr-2" />Register application
				</Button>
			</svelte:fragment>

			{#if applications.length === 0}
				<EmptyState
					title="No application is registered."
					description="Until one is, nothing outside the store can read entitlement state."
				>
					<Button color="primary" size="sm" on:click={() => (isAdding = true)}>
						Register the first application
					</Button>
				</EmptyState>
			{:else}
				<Table divClass="relative overflow-x-auto">
					<TableHead>
						<TableHeadCell>Name</TableHeadCell>
						<TableHeadCell>Key</TableHeadCell>
						<TableHeadCell>Last used</TableHeadCell>
						<TableHeadCell>Scope</TableHeadCell>
						<TableHeadCell>Limits / min</TableHeadCell>
						<TableHeadCell>Active</TableHeadCell>
						<TableHeadCell>Rotate</TableHeadCell>
					</TableHead>
					<TableBody>
						{#each applications as application (application.abstractIDAppUUID)}
							<TableBodyRow>
								<TableBodyCell class="dataTable">
									<div class="font-medium">{application.name}</div>
									<div class="text-default text-xs font-mono">{application.abstractIDAppUUID}</div>
									{#if application.webhookURL}
										<div class="text-default text-xs break-all">{application.webhookURL}</div>
									{:else}
										<div class="text-default text-xs">Pull only</div>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable font-mono">
									{application.apiKeyPrefix ?? '—'}…
									{#if application.previousAPIKeyExpiresAt}
										<div class="text-default text-xs">
											Previous key valid until
											<RelativeDate date={application.previousAPIKeyExpiresAt} />
										</div>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if application.apiKeyLastUsedAt}
										<RelativeDate date={application.apiKeyLastUsedAt} />
									{:else}
										<span class="text-default">Never</span>
									{/if}
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									{#if application.allKeys}
										<Badge color="red">Every key</Badge>
									{:else}
										<span class="text-sm">{scopeLabel(application)}</span>
									{/if}
									<div class="mt-1 flex flex-wrap gap-1">
										<Button
											size="xs"
											color="alternative"
											disabled={busyUUID === application.abstractIDAppUUID}
											on:click={() => fnStartEditingScope(application)}
										>
											Edit keys
										</Button>
										{#if application.allKeys}
											<Button
												size="xs"
												color="alternative"
												disabled={busyUUID === application.abstractIDAppUUID}
												on:click={() => fnPatch(application, { allKeys: false })}
											>
												Scope it down
											</Button>
										{:else}
											<!-- Two-step, because this one flag turns a scoped integration into a
											     reader of every product's customers. -->
											<ConfirmButton
												size="xs"
												color="alternative"
												disabled={busyUUID === application.abstractIDAppUUID}
												confirmText={`Let ${application.name} read every entitlement key in the store, including products it has nothing to do with? This is recorded in the audit trail.`}
												on:confirm={() => fnPatch(application, { allKeys: true })}
											>
												Grant every key
											</ConfirmButton>
										{/if}
									</div>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<div class="flex flex-col gap-1">
										<Input
											type="number"
											size="sm"
											min="1"
											class="w-28"
											placeholder="requests"
											disabled={busyUUID === application.abstractIDAppUUID}
											value={application.rateLimitPerMinute ?? ''}
											on:change={event =>
												fnPatch(application, { rateLimitPerMinute: fnNumberOrNull(event) })}
										/>
										<Input
											type="number"
											size="sm"
											min="1"
											class="w-28"
											placeholder="subjects"
											disabled={busyUUID === application.abstractIDAppUUID}
											value={application.subjectsPerMinute ?? ''}
											on:change={event =>
												fnPatch(application, { subjectsPerMinute: fnNumberOrNull(event) })}
										/>
									</div>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<Toggle
										checked={application.isActive}
										disabled={busyUUID === application.abstractIDAppUUID}
										on:change={event => fnPatch(application, { isActive: fnCheckedFrom(event) })}
									/>
								</TableBodyCell>
								<TableBodyCell class="dataTable">
									<div class="flex flex-col gap-1">
										<Button
											size="xs"
											color="alternative"
											disabled={busyUUID === application.abstractIDAppUUID}
											on:click={() => fnStartDetails(application)}
										>
											<i class="fa-solid fa-pen mr-1" />Edit
										</Button>
										<ConfirmButton
											size="xs"
											color="alternative"
											disabled={busyUUID === application.abstractIDAppUUID}
											confirmText={`Issue a new API key for ${application.name}? The current key keeps working for the overlap window, so nothing goes offline.`}
											on:confirm={() => fnRotate(application, rotateApplicationKey)}
										>
											API key
										</ConfirmButton>
										<ConfirmButton
											size="xs"
											color="alternative"
											disabled={busyUUID === application.abstractIDAppUUID}
											confirmText={`Issue a new signing secret for ${application.name}? Deliveries are signed with both until the overlap window closes.`}
											on:confirm={() => fnRotate(application, rotateApplicationSecret)}
										>
											Secret
										</ConfirmButton>
									</div>
								</TableBodyCell>
							</TableBodyRow>
						{/each}
					</TableBody>
				</Table>
			{/if}
		</Panel>

		{#each applications.filter(row => row.abstractIDAppUUID === editingUUID) as application (application.abstractIDAppUUID)}
			<Panel title={`Entitlement keys for ${application.name}`} faIcon="key">
				<div class="space-y-4">
					<p class="text-default">
						Only these keys are visible to this application, on every endpoint. An empty list means
						it sees nothing.
					</p>

					{#if application.allKeys}
						<!-- The flag grants the keys, not the list. Saying so beats a picker that looks
						     as though it decides something it does not. -->
						<p class="text-sm text-amber-700 dark:text-amber-400">
							<i class="fa-solid fa-triangle-exclamation mr-1" />This application is set to
							<strong>every key</strong>, so the selection below is not consulted while that stands.
						</p>
					{/if}

					{#if catalogueLoading}
						<EmptyState variant="loading" title="Loading the catalogue…" />
					{:else if keyEntryManual}
						<!-- The escape hatch: an application is routinely registered before the product
						     it will read exists, so the picker cannot be the only way to name a key. -->
						<StringListEditor
							id="scope-keys"
							bind:values={editingKeys}
							placeHolder="polyverse_pro"
							color="blue"
						/>
						{#if scopeOptions.length > 0}
							<Button size="xs" color="none" class="!p-0 text-primary-500 hover:underline" on:click={() => (keyEntryManual = false)}>
								Back to the product list
							</Button>
						{/if}
					{:else if scopeOptions.length === 0}
						<EmptyState
							title="No product to scope to yet."
							description="Entitlement keys come from products. Add one in the catalogue, or name a key by hand for an application that will read a product you have not created yet."
						>
							<Button size="sm" color="alternative" on:click={() => (keyEntryManual = true)}>
								Name a key by hand
							</Button>
						</EmptyState>
					{:else}
						<div class="grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="scope-options">
							{#each scopeOptions as option (option.key)}
								<Checkbox
									checked={editingKeys.includes(option.key)}
									on:change={event => fnToggleKey(option.key, fnCheckedFrom(event))}
								>
									<span class="ml-1">
										<span class="font-mono text-xs">{option.key}</span>
										<span class="block text-xs {option.isOrphan ? 'text-amber-700 dark:text-amber-400' : 'text-default'}">
											{#if option.isOrphan}
												<!-- Kept on the list precisely so it can be unticked. Dropping it
												     would revoke it on the next save with nothing said. -->
												<i class="fa-solid fa-triangle-exclamation mr-1" />
											{/if}{option.label}
										</span>
									</span>
								</Checkbox>
							{/each}
						</div>
						<Button size="xs" color="none" class="!p-0 text-primary-500 hover:underline" on:click={() => (keyEntryManual = true)}>
							Name a key by hand
						</Button>
					{/if}

					<div class="flex flex-wrap items-center gap-3">
						<Button
							color="primary"
							disabled={busyUUID === application.abstractIDAppUUID}
							on:click={() => fnSaveScope(application)}
						>
							Save keys
						</Button>
						<Button color="alternative" on:click={() => (editingUUID = '')}>Cancel</Button>
					</div>
				</div>
			</Panel>
		{/each}

		<!-- Delivery health sits under the registry rather than on a page of its own: the question
		     "is this integration receiving anything" is asked about an application, and an operator
		     who has just rotated a secret or narrowed a scope is already looking at this screen. -->
		<WebhookDeliveryTable {applications} />

		{#if editingDetails !== null}
			<FormModal
				open={true}
				title={`Edit ${editingDetails.name}`}
				faIcon="pen"
				submitLabel="Save application"
				size="md"
				busy={busyUUID === editingDetails.abstractIDAppUUID}
				canSubmit={canSaveDetails}
				on:submit={fnSaveDetails}
				on:cancel={() => (detailsUUID = '')}
			>
				<FormField
					id="application-edit-uuid"
					label="AbstractID application"
					description="The identity this application authenticates as. Fixed once registered, because its API key and signing secret are derived against it."
				>
					<Input
						id="application-edit-uuid"
						value={editingDetails.abstractIDAppUUID}
						readonly
						disabled
						class="font-mono"
					/>
				</FormField>

				<FormField id="application-edit-name" label="Name" required>
					<Input id="application-edit-name" bind:value={details.name} disabled={busyUUID !== ''} />
				</FormField>

				<FormField
					id="application-edit-webhook"
					label="Webhook URL"
					description="HTTPS and publicly resolvable. Empty makes it pull-only: the entitlement API still works, nothing is pushed."
				>
					<Input
						id="application-edit-webhook"
						bind:value={details.webhookURL}
						placeholder="https://app.example.com/hooks/entitlement"
						disabled={busyUUID !== ''}
					/>
				</FormField>

				<FormField
					label="Allowed source addresses"
					description="Empty allows any address. Narrowing this to an address the application does not actually call from locks it out of the entitlement API."
				>
					<CIDRListEditor
						id="application-edit-ips"
						bind:values={details.allowedIPs}
						bind:valid={detailsIPsValid}
						disabled={busyUUID !== ''}
					/>
				</FormField>
			</FormModal>
		{/if}

		<FormModal
			bind:open={isAdding}
			title="Register an application"
			faIcon="plug"
			submitLabel="Register application"
			size="lg"
			busy={creating}
			canSubmit={canCreate}
			on:submit={fnCreate}
		>
					<div class="grid md:grid-cols-2 gap-4">
						<!--
							Picked from AbstractID's own directory rather than transcribed. A uuid typed
							by hand is wrong often enough to matter, and a wrong one registers an
							application that exists but is not the one the operator meant — which
							surfaces much later as entitlements that never arrive. The field remains as
							the fallback whenever the directory cannot be read, so a store whose app key
							is unset is inconvenienced rather than blocked.
						-->
						<FormField
							id="application-uuid"
							label="AbstractID application"
							required
							description={pickerUsable
								? 'Pick the application these entitlements belong to.'
								: directoryAvailable && !uuidEntryManual
									? 'Every AbstractID application is already registered here.'
									: directoryReason}
						>
							{#if pickerUsable}
								<SelectField
									id="application-uuid"
									bind:value={draft.abstractIDAppUUID}
									disabled={creating || directoryLoading}
									items={directoryItems}
									placeholder={directoryLoading ? 'Loading applications…' : 'Choose an application'}
								/>
							{:else}
								<Input
									id="application-uuid"
									bind:value={draft.abstractIDAppUUID}
									placeholder="00000000-0000-4000-8000-000000000000"
									disabled={creating}
								/>
							{/if}

							{#if directoryAvailable}
								<button
									type="button"
									class="mt-1 text-xs text-primary-600 hover:underline dark:text-primary-400"
									on:click={() => (uuidEntryManual = !uuidEntryManual)}
								>
									{uuidEntryManual ? 'Choose from the application list' : 'Enter a UUID manually'}
								</button>
							{/if}
						</FormField>

						<FormField id="application-name" label="Name" required>
							<Input id="application-name" bind:value={draft.name} placeholder="Polyverse" disabled={creating} />
						</FormField>

						<FormField
							id="application-webhook"
							label="Webhook URL"
							description="Optional. HTTPS and publicly resolvable; leave empty for a pull-only integration."
						>
							<Input
								id="application-webhook"
								bind:value={draft.webhookURL}
								placeholder="https://app.example.com/hooks/entitlement"
								disabled={creating}
							/>
						</FormField>
					</div>

					<FormField
						label="Allowed source addresses"
						description="Optional. Empty allows any address. Narrowing this to an address the application does not actually call from locks it out of the entitlement API."
					>
						<CIDRListEditor
							id="application-ips"
							bind:values={draft.allowedIPs}
							bind:valid={draftIPsValid}
							disabled={creating}
						/>
					</FormField>

					<p class="text-default text-sm">
						A new application starts scoped to <strong>no keys</strong>. Add them after registering.
					</p>
		</FormModal>
	</div>
{/if}
